import React, { useContext, useEffect, useState } from 'react'

import {
  Stack,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
} from '@mui/material'
import API from '../../../../services/API'
import { UserContext } from '../../../../App'
import { Add, Remove } from '@mui/icons-material'
import BigModal from '../../../../components/Modals/BigModal'
import BillingProrationSummary from './BillingProrationSummary'

// Buying (or giving back) extra employee seats. Employees in use against the
// plan's capacity are drawn once, by the Employees line of <UsageAllowances />
// directly above this card on the Billing page, so there is no capacity bar
// of its own here.
const EmployeeUsage = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [extraEmployees, setExtraEmployees] = useState(0)
  const [newInvoiceData, setNewInvoiceData] = useState(null)
  const [loadingStripeAPI, setLoadingStripeAPI] = useState(false)

  const minimumExtra = Math.max(
    0,
    (data?.employeeCount ?? 0) -
      (data?.includedEmployees ?? 0) -
      (data?.freeEmployees ?? 0),
  )

  const clamp = (n, min, max = Infinity) => Math.max(min, Math.min(max, n))

  const handleUpdateAdditionalEmployeeCount = async (e) => {
    e.preventDefault()

    setLoadingStripeAPI(true)

    const qty = clamp(Number(extraEmployees || 0), minimumExtra)

    // 1️⃣ Preview
    await API.post('/billing/employeeusage/preview', {
      quantity: qty,
    }).then((res) => {
      if (res.status === 200) {
        setNewInvoiceData(res.data)

        setLoadingStripeAPI(false)
      }
    })
  }

  const reload = () =>
    API.get('/billing/employeeusage')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setExtraEmployees(res.data?.extraEmployees ?? 0)
        }
      })
      .finally(() => setLoading(false))

  const handleUpdateAdditionalEmployeeCountConfirm = async () => {
    setLoadingStripeAPI(true)
    await API.post('/billing/employeeusage/update', {
      quantity: clamp(Number(extraEmployees || 0), minimumExtra),
    }).then((res) => {
      if (res.status === 200) {
        setLoadingStripeAPI(false)
        setNewInvoiceData(null)
        window.location.reload()
      }
    })
    // window.location.reload()
  }

  useEffect(() => {
    reload()
  }, [])

  return (
    <Stack component={Paper} sx={{ p: 3, mb: 4, border: 'solid 1px' }} gap={1}>
      <Typography variant="h5">Additional Employees</Typography>
      <Typography variant="body2" color="text.secondary">
        Your current subscription allows for {data?.includedEmployees}{' '}
        employees. You currently have {data?.employeeCount} active employees.
        Additional employees are billed at £2 per employee.
      </Typography>
      <Typography variant="h6">Additional Employee Capacity</Typography>
      <Typography variant="body2" color="text.secondary">
        Enter how many employees you want to add above your plan’s included{' '}
        {data?.includedEmployees}. Additional employees are billed at £2 per
        employee per month. Once edited, your card will be charged the
        difference for the rest of your billing period and then continue to be
        charged monthly.
      </Typography>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        component="form"
        onSubmit={handleUpdateAdditionalEmployeeCount}
        gap={1}
      >
        <Stack gap={1} direction="row" alignItems="center">
          <IconButton
            onClick={() =>
              setExtraEmployees((prev) => clamp(Number(prev) - 1, minimumExtra))
            }
            disabled={loadingStripeAPI}
          >
            <Remove />
          </IconButton>
          <TextField
            type="number"
            inputProps={{
              min: minimumExtra,
            }}
            value={extraEmployees}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') return setExtraEmployees('')

              const next = Number(raw)
              if (Number.isNaN(next)) return

              setExtraEmployees(clamp(next, minimumExtra))
            }}
            required
            autoComplete="none"
            disabled={loadingStripeAPI}
          />
          <IconButton
            onClick={() =>
              setExtraEmployees((prev) => clamp(Number(prev) + 1, minimumExtra))
            }
            disabled={loadingStripeAPI}
          >
            <Add />
          </IconButton>
        </Stack>

        <Button
          type="submit"
          variant="contained"
          color={extraEmployees < data?.extraEmployees ? 'error' : 'success'}
          disabled={
            loadingStripeAPI ||
            extraEmployees === data?.extraEmployees ||
            isNaN(extraEmployees)
          }
        >
          {extraEmployees > data?.extraEmployees
            ? 'Purchase Additional Employee Capacity'
            : extraEmployees < data?.extraEmployees
              ? 'Delete Additional Employee Capacity'
              : 'Update'}
        </Button>
      </Stack>
      <BigModal
        style={{ padding: 0, borderRadius: 3 }}
        open={!!newInvoiceData}
        onClose={() => setNewInvoiceData(null)}
      >
        <BillingProrationSummary
          currency={newInvoiceData?.currency}
          prorationDueNow={newInvoiceData?.prorationDueNow}
          newMonthly={newInvoiceData?.newRecurringTotal}
          interval={newInvoiceData?.interval}
          {...(newInvoiceData?.addonBilling && {
            recurringLabel: 'Monthly seat total',
            recurringCaption:
              'Extra employees are billed monthly, separately from your annual plan.',
          })}
          onConfirm={() => handleUpdateAdditionalEmployeeCountConfirm()}
          onCancel={() => setNewInvoiceData(null)}
          confirmLabel="Confirm and Charge"
          loading={loadingStripeAPI}
        />
      </BigModal>
    </Stack>
  )
}

export default EmployeeUsage
